import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, TextField, InputAdornment, Chip, Avatar,
  IconButton, Tooltip, Stack, Select, MenuItem, FormControl, InputLabel,
  Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, CircularProgress, Badge,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Star as StarIcon,
  Flag as FlagIcon,
  PersonAdd as AssignIcon,
} from "@mui/icons-material";
import {
  fetchLeadsWithMeta,
  type LeadThread, type LeadStatus, type PaginationMeta,
  STATUS_CONFIG, AI_SCORE_CONFIG, PRIORITY_CONFIG,
} from "./leadApi";
import LeadThreadDrawer from "./LeadThreadDrawer";
import CreateLeadDialog from "./CreateLeadDialog";

const NAVY = "#0d1b2a";
const GOLD = "#c9a96e";

export default function LeadInquiries() {
  const [leads, setLeads] = useState<LeadThread[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const filters: Record<string, any> = {
        source: "website",
        page: page + 1,
        per_page: perPage,
      };
      if (search) filters.search = search;
      if (statusFilter) filters.status = statusFilter;

      const result = await fetchLeadsWithMeta(filters);
      setLeads(result.data);
      setMeta(result.meta);
    } catch {}
    setLoading(false);
  }, [page, perPage, search, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openThread = (id: number) => {
    setSelectedId(id);
    setDrawerOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>
            Website Inquiries
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Leads from website contact form &middot; {meta?.total ?? 0} total
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1b2e44" } }}
          >
            New Inquiry
          </Button>
          <IconButton onClick={loadData}>
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: "1px solid #e5e7eb",
          borderRadius: 2,
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          placeholder="Search by name, email, phone..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280 }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <MenuItem key={k} value={k}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: v.color }} />
                  {v.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Event</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }} align="center">Guests</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Score</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Assigned</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} sx={{ color: GOLD }} />
                  </TableCell>
                </TableRow>
              ) : leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: "#94a3b8" }}>
                    No inquiries found
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead) => (
                  <TableRow
                    key={lead.id}
                    hover
                    sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f8fafc" } }}
                    onClick={() => openThread(lead.id)}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: GOLD,
                            color: NAVY,
                            fontSize: "0.8rem",
                            fontWeight: 700,
                          }}
                        >
                          {lead.name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: NAVY }}>
                            {lead.name}
                            {lead.is_vip && (
                              <StarIcon sx={{ color: GOLD, fontSize: 14, ml: 0.5, verticalAlign: "middle" }} />
                            )}
                          </Typography>
                          {lead.organization && (
                            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                              {lead.organization}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: "#475569", display: "block" }}>
                        {lead.email || "—"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                        {lead.phone || ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                        {lead.event_type?.replace(/_/g, " ") || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{lead.guest_count ?? "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {lead.event_date
                          ? new Date(lead.event_date + "T00:00:00").toLocaleDateString("en-CA", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={STATUS_CONFIG[lead.status]?.label}
                        sx={{
                          bgcolor: STATUS_CONFIG[lead.status]?.bgColor,
                          color: STATUS_CONFIG[lead.status]?.color,
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          height: 24,
                          border: `1px solid ${STATUS_CONFIG[lead.status]?.color}30`,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {lead.ai_score ? (
                        <Chip
                          size="small"
                          label={`${AI_SCORE_CONFIG[lead.ai_score].icon} ${AI_SCORE_CONFIG[lead.ai_score].label}`}
                          sx={{
                            bgcolor: `${AI_SCORE_CONFIG[lead.ai_score].color}15`,
                            color: AI_SCORE_CONFIG[lead.ai_score].color,
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            height: 24,
                          }}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        {lead.assignee?.username || "Unassigned"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {meta && (
          <TablePagination
            component="div"
            count={meta.total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => {
              setPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        )}
      </Paper>

      {/* Thread Drawer */}
      <LeadThreadDrawer
        open={drawerOpen}
        threadId={selectedId}
        onClose={() => setDrawerOpen(false)}
        onUpdated={loadData}
      />

      {/* Create Dialog */}
      <CreateLeadDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          loadData();
        }}
        defaultSource="website"
      />
    </Box>
  );
}
